import React, { useState } from "react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import RLInventoryMonthlyReport from "@/modules/racket_lab_admin/RLInventoryMonthlyReport";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminInventory — SS-6 (Jun 2026).
 *
 * Admin-only string & consumables inventory per shop:
 *   - list + filters (shop / type / status),
 *   - create / edit (descriptive fields only),
 *   - auditable stock adjustment (signed delta + mandatory reason,
 *     reversible via compensating adjustment),
 *   - activate / deactivate (NO hard-delete anywhere),
 *   - append-only movements history.
 *
 * RBAC: writes for admin/staff/shop_manager (scoped); qa_manager and
 * operators read-only; qa_tester never reaches this page (no section).
 *
 * HARD BLOCKS preserved: no customer-facing, no real sends, no legal
 * side effects, automatic consumption deferred to SS-6.1.
 */
const ITEM_TYPES = ["string_set", "string_reel", "consumable"];
const UNITS = ["set", "meter", "unit"];
const INPUT_CLS = "rounded-md border border-slate-300 px-2 py-1.5 text-sm";

const _Field = ({ label, children, hint, testId }) => (
  <label data-testid={testId} className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    {children}
    {hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}
  </label>
);

const _Modal = ({ children, testId }) => (
  <div data-testid={testId} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
    <div className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-lg">
      {children}
    </div>
  </div>
);

const ItemFormDialog = ({ item, shops, onClose, onSaved }) => {
  const t = useRacketLabAdminT();
  const { run, loading } = useAdminMutation();
  const isEdit = Boolean(item);
  const [form, setForm] = useState({
    racket_lab_shop_id: item?.racket_lab_shop_id || "",
    item_type: item?.item_type || "string_set",
    brand_label: item?.brand_label || "",
    model_label: item?.model_label || "",
    gauge_label: item?.gauge_label || "",
    unit: item?.unit || "set",
    initial_quantity: "",
    consumption_per_ticket: item?.consumption_per_ticket ?? 1,
    low_stock_threshold: item?.low_stock_threshold ?? "",
    unit_cost: item?.unit_cost ?? "",
    notes_internal: item?.notes_internal || "",
    catalog_product_id: item?.catalog_product_id || "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const ok = (isEdit || form.racket_lab_shop_id) && form.brand_label.trim() && form.model_label.trim();

  // CATALOG-4 — optional central-catalog product (strings + technical
  // accessories). Picking one autofills brand/model and stores an ADDITIVE
  // ref; manual entry stays fully functional (create mode only).
  const { data: catData } = useAdminApi(isEdit ? null : "/admin/racket-lab/catalog/products", {
    params: { limit: 500 },
  });
  const catProducts = (catData?.items || []).filter(
    (p) => p.type === "string" || p.type === "accessory",
  );
  const onCatalogPick = (pid) =>
    setForm((s) => {
      const p = catProducts.find((x) => x.catalog_product_id === pid);
      if (!p) return { ...s, catalog_product_id: "" };
      return {
        ...s,
        catalog_product_id: p.catalog_product_id,
        brand_label: (p.brand_name || s.brand_label).slice(0, 80),
        model_label: p.label.slice(0, 120),
      };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!ok || loading) return;
    const numericExtras = {
      consumption_per_ticket: form.consumption_per_ticket === "" ? 1 : parseFloat(form.consumption_per_ticket),
      low_stock_threshold: form.low_stock_threshold === "" ? null : parseFloat(form.low_stock_threshold),
      unit_cost: form.unit_cost === "" ? null : parseFloat(form.unit_cost),
    };
    const body = isEdit
      ? {
          brand_label: form.brand_label.trim(),
          model_label: form.model_label.trim(),
          gauge_label: form.gauge_label.trim() || null,
          unit: form.unit,
          notes_internal: form.notes_internal.trim() || null,
          ...numericExtras,
        }
      : {
          racket_lab_shop_id: form.racket_lab_shop_id,
          item_type: form.item_type,
          brand_label: form.brand_label.trim(),
          model_label: form.model_label.trim(),
          unit: form.unit,
          initial_quantity: form.initial_quantity === "" ? 0 : parseFloat(form.initial_quantity),
          ...numericExtras,
          ...(form.gauge_label.trim() ? { gauge_label: form.gauge_label.trim() } : {}),
          ...(form.notes_internal.trim() ? { notes_internal: form.notes_internal.trim() } : {}),
          ...(form.catalog_product_id ? { catalog_product_id: form.catalog_product_id } : {}),
        };
    const res = await run(
      isEdit ? "patch" : "post",
      isEdit ? `/admin/racket-lab/inventory/${item.id}` : "/admin/racket-lab/inventory",
      body,
    ).catch(() => null);
    if (res) onSaved();
  };

  return (
    <_Modal testId="rl-inv-form-dialog">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">
        {isEdit ? t("inventory.form.title_edit") : t("inventory.form.title_create")}
      </h2>
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {!isEdit && (
          <_Field testId="rl-inv-form-field-shop" label={t("inventory.form.shop")}>
            <select data-testid="rl-inv-form-shop-select" value={form.racket_lab_shop_id}
              onChange={(e) => set("racket_lab_shop_id", e.target.value)} className={INPUT_CLS} required>
              <option value="">{t("inventory.form.shop_placeholder")}</option>
              {shops.map((s) => (<option key={s.id} value={s.id}>{s.name || s.id}</option>))}
            </select>
          </_Field>
        )}
        {!isEdit && (
          <_Field testId="rl-inv-form-field-type" label={t("inventory.form.type")}>
            <select data-testid="rl-inv-form-type-select" value={form.item_type}
              onChange={(e) => set("item_type", e.target.value)} className={INPUT_CLS}>
              {ITEM_TYPES.map((it) => (<option key={it} value={it}>{t(`inventory.types.${it}`)}</option>))}
            </select>
          </_Field>
        )}
        {!isEdit && catProducts.length > 0 && (
          <_Field testId="rl-inv-form-field-catalog" label={t("inventory.form_catalog.label")} hint={t("inventory.form_catalog.hint")}>
            <select
              data-testid="rl-inv-form-catalog-select"
              value={form.catalog_product_id}
              onChange={(e) => onCatalogPick(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">{t("inventory.form_catalog.placeholder")}</option>
              {catProducts.map((p) => (
                <option key={p.catalog_product_id} value={p.catalog_product_id}>
                  {p.label}{p.type === "accessory" && p.subcategory ? ` · ${p.subcategory}` : ""}
                </option>
              ))}
            </select>
          </_Field>
        )}
        <_Field testId="rl-inv-form-field-brand" label={t("inventory.form.brand")} hint={t("inventory.form.anti_pii_hint")}>
          <input data-testid="rl-inv-form-brand-input" type="text" value={form.brand_label} maxLength={80}
            onChange={(e) => set("brand_label", e.target.value)} placeholder={t("inventory.form.brand_placeholder")} className={INPUT_CLS} required />
        </_Field>
        <_Field testId="rl-inv-form-field-model" label={t("inventory.form.model")}>
          <input data-testid="rl-inv-form-model-input" type="text" value={form.model_label} maxLength={120}
            onChange={(e) => set("model_label", e.target.value)} placeholder={t("inventory.form.model_placeholder")} className={INPUT_CLS} required />
        </_Field>
        <_Field testId="rl-inv-form-field-gauge" label={t("inventory.form.gauge")}>
          <input data-testid="rl-inv-form-gauge-input" type="text" value={form.gauge_label} maxLength={40}
            onChange={(e) => set("gauge_label", e.target.value)} placeholder={t("inventory.form.gauge_placeholder")} className={INPUT_CLS} />
        </_Field>
        <_Field testId="rl-inv-form-field-unit" label={t("inventory.form.unit")}>
          <select data-testid="rl-inv-form-unit-select" value={form.unit}
            onChange={(e) => set("unit", e.target.value)} className={INPUT_CLS}>
            {UNITS.map((u) => (<option key={u} value={u}>{t(`inventory.units.${u}`)}</option>))}
          </select>
        </_Field>
        {!isEdit && (
          <_Field testId="rl-inv-form-field-initial" label={t("inventory.form.initial_quantity")}>
            <input data-testid="rl-inv-form-initial-input" type="number" min={0} max={100000} step="0.01"
              value={form.initial_quantity} onChange={(e) => set("initial_quantity", e.target.value)} className={INPUT_CLS} />
          </_Field>
        )}
        <_Field testId="rl-inv-form-field-consumption" label={t("inventory.form.consumption")} hint={t("inventory.form.consumption_hint")}>
          <input data-testid="rl-inv-form-consumption-input" type="number" min={0} max={1000} step="0.01"
            value={form.consumption_per_ticket} onChange={(e) => set("consumption_per_ticket", e.target.value)} className={INPUT_CLS} />
        </_Field>
        <_Field testId="rl-inv-form-field-low-stock" label={t("inventory.form.low_stock")} hint={t("inventory.form.low_stock_hint")}>
          <input data-testid="rl-inv-form-low-stock-input" type="number" min={0} max={100000} step="0.01"
            value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className={INPUT_CLS} />
        </_Field>
        <_Field testId="rl-inv-form-field-unit-cost" label={t("inventory.form.unit_cost")} hint={t("inventory.form.unit_cost_hint")}>
          <input data-testid="rl-inv-form-unit-cost-input" type="number" min={0} max={100000} step="0.01"
            value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} className={INPUT_CLS} />
        </_Field>
        <_Field testId="rl-inv-form-field-notes" label={t("inventory.form.notes")} hint={t("inventory.form.anti_pii_hint")}>
          <textarea data-testid="rl-inv-form-notes-input" value={form.notes_internal} maxLength={2000} rows={2}
            onChange={(e) => set("notes_internal", e.target.value)} className={INPUT_CLS} />
        </_Field>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3 md:col-span-2">
          <button type="button" data-testid="rl-inv-form-cancel" onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
            {t("inventory.form.cancel")}
          </button>
          <button type="submit" data-testid="rl-inv-form-submit" disabled={!ok || loading}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
            {loading ? t("inventory.form.submitting") : isEdit ? t("inventory.form.submit_edit") : t("inventory.form.submit_create")}
          </button>
        </div>
      </form>
    </_Modal>
  );
};

const AdjustDialog = ({ item, onClose, onSaved }) => {
  const t = useRacketLabAdminT();
  const { run, loading } = useAdminMutation();
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const parsed = parseFloat(delta);
  const valid = !Number.isNaN(parsed) && parsed !== 0 && reason.trim().length > 0;
  const wouldGoNegative = !Number.isNaN(parsed) && item.available_quantity + parsed < 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    const res = await run("post", `/admin/racket-lab/inventory/${item.id}/adjust`, {
      delta: parsed, reason: reason.trim(),
    }).catch(() => null);
    if (res) onSaved();
  };

  return (
    <_Modal testId="rl-inv-adjust-dialog">
      <h2 className="mb-1 text-sm font-semibold text-slate-900">{t("inventory.adjust.title")}</h2>
      <p className="mb-3 text-xs text-slate-500">
        {item.brand_label} {item.model_label} · {t("inventory.adjust.current")}:{" "}
        <strong data-testid="rl-inv-adjust-current">{item.available_quantity}</strong> {t(`inventory.units.${item.unit}`)}
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <_Field testId="rl-inv-adjust-field-delta" label={t("inventory.adjust.delta")} hint={t("inventory.adjust.delta_hint")}>
          <input data-testid="rl-inv-adjust-delta-input" type="number" step="0.01" value={delta}
            onChange={(e) => setDelta(e.target.value)} className={INPUT_CLS} required />
        </_Field>
        <_Field testId="rl-inv-adjust-field-reason" label={t("inventory.adjust.reason")}>
          <input data-testid="rl-inv-adjust-reason-input" type="text" value={reason} maxLength={400}
            onChange={(e) => setReason(e.target.value)} placeholder={t("inventory.adjust.reason_placeholder")} className={INPUT_CLS} required />
        </_Field>
        {wouldGoNegative ? (
          <p data-testid="rl-inv-adjust-negative-warning" className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
            {t("inventory.adjust.insufficient")}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <button type="button" data-testid="rl-inv-adjust-cancel" onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
            {t("inventory.adjust.cancel")}
          </button>
          <button type="submit" data-testid="rl-inv-adjust-submit" disabled={!valid || wouldGoNegative || loading}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
            {loading ? t("inventory.adjust.submitting") : t("inventory.adjust.submit")}
          </button>
        </div>
      </form>
    </_Modal>
  );
};

const MovementsDialog = ({ item, canWrite, onClose, onStockChanged }) => {
  const t = useRacketLabAdminT();
  const { data, loading, error, reload } = useAdminApi(`/admin/racket-lab/inventory/${item.id}/movements`);
  const { run, loading: compensating } = useAdminMutation();
  const rows = data?.items || [];
  const compensate = async (m) => {
    const res = await run("post", `/admin/racket-lab/inventory/movements/${m.id}/compensate`, {}).catch(() => null);
    if (res) { reload(); onStockChanged(); }
  };
  return (
    <_Modal testId="rl-inv-movements-dialog">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">
          {t("inventory.movements.title")} · {item.brand_label} {item.model_label}
        </h2>
        <button type="button" data-testid="rl-inv-movements-close" onClick={onClose}
          className="text-xs text-slate-500 hover:text-slate-900">
          {t("inventory.movements.close")}
        </button>
      </div>
      <AdminApiTable
        columns={[
          { key: "created_at", label: t("inventory.movements.columns.at"), render: (m) => (
            <span className="text-[10px] text-slate-500">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
          )},
          { key: "movement_type", label: t("inventory.movements.columns.type"), render: (m) => (
            <span>
              {t(`inventory.movements.type_${m.movement_type}`)}
              {m.ticket_id ? <span className="ml-1 text-[9px] text-slate-400">#{m.ticket_id.slice(0, 8)}</span> : null}
            </span>
          )},
          { key: "delta", label: t("inventory.movements.columns.delta"), render: (m) => (
            <span className={m.delta < 0 ? "text-rose-700" : "text-emerald-700"}>{m.delta > 0 ? `+${m.delta}` : m.delta}</span>
          )},
          { key: "range", label: t("inventory.movements.columns.range"), render: (m) => `${m.quantity_before} → ${m.quantity_after}` },
          { key: "reason", label: t("inventory.movements.columns.reason"), render: (m) => (
            <span className="text-xs text-slate-600">{m.reason || "—"}</span>
          )},
          { key: "actions", label: "", render: (m) => (
            m.movement_type !== "consumption" ? null : m.compensated ? (
              <span data-testid={`rl-inv-mov-compensated-${m.id}`} className="text-[9px] text-slate-400">
                {t("inventory.movements.compensated")}
              </span>
            ) : canWrite ? (
              <button type="button" data-testid={`rl-inv-mov-compensate-${m.id}`} disabled={compensating}
                onClick={() => compensate(m)}
                className="rounded bg-amber-100 px-2 py-1 text-[10px] text-amber-800 hover:bg-amber-200 disabled:opacity-60">
                {t("inventory.movements.compensate")}
              </button>
            ) : null
          )},
        ]}
        rows={rows} loading={loading} error={error}
        emptyLabel={t("inventory.movements.empty")} dataTestId="rl-inv-movements-table"
      />
    </_Modal>
  );
};

const RLAdminInventory = () => {
  const t = useRacketLabAdminT();
  const { ctx } = useRLContext();
  const canWrite = ["admin", "staff", "shop_manager"].includes(ctx?.profile);
  const [filters, setFilters] = useState({ shop: "", type: "", status: "" });
  // micro (Jun 2026) — read-only "low stock only" client-side filter on the
  // computed SS-6.1 `low_stock` flag; pre-activated via ?low_stock=1 (the
  // dashboard low-stock tile deep-links here). Zero new API surface.
  const [lowOnly, setLowOnly] = useState(
    () => new URLSearchParams(window.location.search).get("low_stock") === "1",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [adjustItem, setAdjustItem] = useState(null);
  const [movementsItem, setMovementsItem] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const { data: shopsData } = useAdminApi("/admin/racket-lab/shops", { params: { limit: 200 } });
  const shops = shopsData?.items || [];
  const shopName = Object.fromEntries(shops.map((s) => [s.id, s.name || s.id]));

  const params = { limit: 200 };
  if (filters.shop) params.racket_lab_shop_id = filters.shop;
  if (filters.type) params.item_type = filters.type;
  if (filters.status) params.status = filters.status;
  const list = useAdminApi("/admin/racket-lab/inventory", { params });
  const allRows = list.data?.items || [];
  const rows = lowOnly ? allRows.filter((r) => r.low_stock) : allRows;

  const { run, loading: mutating } = useAdminMutation();
  const toggleStatus = (item) => async () => {
    const to = item.status === "active" ? "inactive" : "active";
    await run("post", `/admin/racket-lab/inventory/${item.id}/status`, { to }).catch(() => {});
    list.reload();
  };

  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="rl-admin-inventory-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t("inventory.page_header")}</h1>
          <p className="text-xs text-slate-500">{t("inventory.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="rl-inv-report-toggle" onClick={() => setReportOpen((v) => !v)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
            {t("inventory.report.toggle")}
          </button>
          {canWrite && (
            <button type="button" data-testid="rl-inventory-new-btn" onClick={() => setCreateOpen(true)}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700">
              {t("inventory.new_btn")}
            </button>
          )}
        </div>
      </header>

      {reportOpen && <RLInventoryMonthlyReport shops={shops} shopName={shopName} />}

      <p data-testid="rl-inventory-ss61-note" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
        {t("inventory.consumption_note")}
      </p>

      {!canWrite && (
        <p data-testid="rl-inventory-readonly-note" className="rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-[11px] text-slate-600">
          {t("inventory.readonly_note")}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <select data-testid="rl-inventory-filter-shop" value={filters.shop} onChange={(e) => setF("shop", e.target.value)} className={INPUT_CLS}>
          <option value="">{t("inventory.filters.shop")} · {t("inventory.filters.all")}</option>
          {shops.map((s) => (<option key={s.id} value={s.id}>{s.name || s.id}</option>))}
        </select>
        <select data-testid="rl-inventory-filter-type" value={filters.type} onChange={(e) => setF("type", e.target.value)} className={INPUT_CLS}>
          <option value="">{t("inventory.filters.type")} · {t("inventory.filters.all")}</option>
          {ITEM_TYPES.map((it) => (<option key={it} value={it}>{t(`inventory.types.${it}`)}</option>))}
        </select>
        <select data-testid="rl-inventory-filter-status" value={filters.status} onChange={(e) => setF("status", e.target.value)} className={INPUT_CLS}>
          <option value="">{t("inventory.filters.status")} · {t("inventory.filters.all")}</option>
          <option value="active">{t("inventory.status_active")}</option>
          <option value="inactive">{t("inventory.status_inactive")}</option>
        </select>
        <label className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="rl-inventory-filter-low-stock"
            checked={lowOnly}
            onChange={(e) => setLowOnly(e.target.checked)}
          />
          {t("inventory.filters.low_stock_only")}
        </label>
      </div>

      <AdminApiTable
        columns={[
          { key: "shop", label: t("inventory.columns.shop"), render: (r) => (
            <span className="text-xs">{shopName[r.racket_lab_shop_id] || r.racket_lab_shop_id?.slice(0, 12)}</span>
          )},
          { key: "type", label: t("inventory.columns.type"), render: (r) => t(`inventory.types.${r.item_type}`) },
          { key: "item", label: t("inventory.columns.item"), render: (r) => (
            <span data-testid={`rl-inv-item-label-${r.id}`} className="font-medium">
              {r.brand_label} {r.model_label}{r.gauge_label ? ` · ${r.gauge_label}` : ""}
            </span>
          )},
          { key: "stock", label: t("inventory.columns.stock"), render: (r) => (
            <span data-testid={`rl-inv-qty-${r.id}`} className={r.available_quantity <= 0 ? "font-semibold text-rose-700" : ""}>
              {r.available_quantity} / {r.initial_quantity} {t(`inventory.units.${r.unit}`)}
              {r.low_stock ? (
                <span data-testid={`rl-inv-low-badge-${r.id}`} className="ml-1 inline-flex rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700">
                  {t("inventory.low_badge")}
                  {r.low_stock_threshold_source === "shop" ? (
                    <span
                      data-testid={`rl-inv-low-source-shop-${r.id}`}
                      title={t("inventory.low_source_shop_hint")}
                      className="ml-1 rounded bg-rose-200 px-1 text-[8px]"
                    >
                      {t("inventory.low_source_shop")}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </span>
          )},
          { key: "status", label: t("inventory.columns.status"), render: (r) => (
            <span data-testid={`rl-inv-status-${r.id}`} className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
              r.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
            }`}>{t(`inventory.status_${r.status}`)}</span>
          )},
          { key: "actions", label: t("inventory.columns.actions"), render: (r) => (
            <div className="flex flex-wrap gap-1">
              <button type="button" data-testid={`rl-inv-movements-${r.id}`} onClick={() => setMovementsItem(r)}
                className="rounded bg-slate-100 px-2 py-1 text-[10px] hover:bg-slate-200">
                {t("inventory.actions.movements")}
              </button>
              {canWrite && (
                <>
                  <button type="button" data-testid={`rl-inv-adjust-${r.id}`} onClick={() => setAdjustItem(r)}
                    className="rounded bg-emerald-100 px-2 py-1 text-[10px] text-emerald-800 hover:bg-emerald-200">
                    {t("inventory.actions.adjust")}
                  </button>
                  <button type="button" data-testid={`rl-inv-edit-${r.id}`} onClick={() => setEditItem(r)}
                    className="rounded bg-slate-100 px-2 py-1 text-[10px] hover:bg-slate-200">
                    {t("inventory.actions.edit")}
                  </button>
                  <ConfirmActionDialog title={t("inventory.toggle_title")} confirmLabel={t("inventory.toggle_confirm")}
                    onConfirm={toggleStatus(r)} disabled={mutating} dataTestId={`rl-inv-toggle-${r.id}`}
                    trigger={<button type="button" className="rounded bg-slate-100 px-2 py-1 text-[10px] hover:bg-slate-200">
                      {r.status === "active" ? t("inventory.actions.deactivate") : t("inventory.actions.activate")}
                    </button>}
                  />
                </>
              )}
            </div>
          )},
        ]}
        rows={rows} loading={list.loading} error={list.error}
        emptyLabel={t("inventory.empty")} dataTestId="rl-inventory-table"
      />

      {createOpen && (
        <ItemFormDialog shops={shops} onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); list.reload(); }} />
      )}
      {editItem && (
        <ItemFormDialog item={editItem} shops={shops} onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); list.reload(); }} />
      )}
      {adjustItem && (
        <AdjustDialog item={adjustItem} onClose={() => setAdjustItem(null)}
          onSaved={() => { setAdjustItem(null); list.reload(); }} />
      )}
      {movementsItem && (
        <MovementsDialog item={movementsItem} canWrite={canWrite}
          onClose={() => setMovementsItem(null)} onStockChanged={() => list.reload()} />
      )}
    </div>
  );
};

export default RLAdminInventory;

